import { Fragment, useState, type FormEvent } from 'react';
import type {
  AssessmentRecordResponse,
  AssessmentRecordsResponse,
  AttributeDefinitionResponse,
  SourceTypeCode,
} from '../../api/types';
import { apiPost, describeError } from '../../api/client';
import { useApiResource } from '../../hooks/useApiResource';
import { EmptyState, ErrorState, LoadingState } from '../StateMessage';
import { evidenceTypeLabel, formatCalendarDate, formatTimestamp, sourceLabel } from './attributeDisplay';
import { AssessmentForm } from './AssessmentForm';
import styles from './AssessmentRecords.module.css';

/**
 * The individual assessments behind one attribute, and the two things that
 * can be done to your own (Mission 015).
 *
 * ---------------------------------------------------------------------
 * WHY THIS EXISTS AT ALL
 *
 * Correcting or withdrawing an assessment means naming one, and until this
 * mission nothing in the product could. The attribute row shows a composite
 * and a per-*source* breakdown; the history shows computed snapshots. A
 * source line saying "Manager 16" is not a record you can act on — it may
 * be one assessment or the average of several, and it carries no identity.
 *
 * It is also the only place a superseded or withdrawn record stays visible.
 * The composite, the confidence calculation, the spread and the history
 * points all filter them out, so without this list a "preserved" original
 * would exist in the database and nowhere a person could see it — which is
 * only technically a preservation.
 *
 * WHAT IT DOES NOT DECIDE
 *
 * `effective` — whether a record still counts — is computed by the domain
 * and read from the response. This component never derives it from
 * `recordStatus` and `supersededByAssessmentId`, because a browser
 * re-deriving lifecycle state is exactly how a list and a composite come to
 * disagree about the same records.
 *
 * Whether an action is offered follows `recordedByViewer` and `effective`,
 * both from the API. Both are re-checked server-side, so the affordance is
 * a courtesy rather than the rule.
 *
 * EVIDENCE STAYS ATTACHED TO THE RECORD IT WAS ATTACHED TO
 * (docs/DOMAIN_DECISIONS.md, "Evidence Attachment Write Contract" EA-1/EA-2)
 *
 * Each record renders exactly the `evidence` array the API sent for it —
 * never a second fetch, never evidence borrowed from another record.
 * `evidenceCount` and `evidence.length` can legitimately differ
 * (`evidenceCount` counts every active item; `evidence` is filtered to
 * what this viewer may see — RE-1/RE-2), and when they do, this component
 * says a further item exists without guessing at what it is or why —
 * exactly the asymmetry the DTO's own documentation names.
 * ---------------------------------------------------------------------
 */
export function AssessmentRecords({
  employeeId,
  employeeName,
  attribute,
  viewerCanAssessAs,
  regionId,
  onChanged,
}: {
  employeeId: string;
  employeeName: string;
  attribute: AttributeDefinitionResponse;
  viewerCanAssessAs: SourceTypeCode | null;
  regionId: string;
  /** Re-reads every authoritative figure on the page. */
  onChanged: () => void;
}) {
  const state = useApiResource<AssessmentRecordsResponse>(
    `/employees/${employeeId}/attributes/${attribute.id}/assessments`
  );
  /** The record currently being corrected or withdrawn, if any. Only one at
   * a time: two half-finished lifecycle actions on one attribute is a way
   * to submit the wrong one. */
  const [acting, setActing] = useState<{ mode: 'correct' | 'withdraw'; record: AssessmentRecordResponse } | null>(null);

  function finish() {
    setActing(null);
    onChanged();
  }

  if (state.status === 'loading') {
    return (
      <div className={styles.panel} id={regionId}>
        <LoadingState label="Loading recorded assessments…" />
      </div>
    );
  }

  if (state.status === 'failed') {
    return (
      <div className={styles.panel} id={regionId}>
        <ErrorState title="These assessments could not be loaded" detail={describeError(state.error)} />
      </div>
    );
  }

  const { assessments, effectiveCount } = state.data;

  if (assessments.length === 0) {
    return (
      <div className={styles.panel} id={regionId}>
        <EmptyState
          title="No assessments recorded"
          detail={`Nothing has been recorded for ${attribute.name}, so there is nothing to correct or withdraw.`}
        />
      </div>
    );
  }

  return (
    <div className={styles.panel} id={regionId}>
      <p className={styles.summary}>
        {assessments.length} {assessments.length === 1 ? 'record' : 'records'}, of which {effectiveCount}{' '}
        {effectiveCount === 1 ? 'counts' : 'count'} towards the current figures. Replaced and withdrawn assessments
        stay listed — nothing here is ever deleted.
      </p>

      <table className={styles.table}>
        <caption className={styles.caption}>
          Every assessment recorded for {attribute.name}, oldest first. Who made each one is not shown; the
          perspective it was recorded from is.
        </caption>
        <thead>
          <tr>
            <th scope="col">Recorded</th>
            <th scope="col">Rating</th>
            <th scope="col">Perspective</th>
            <th scope="col">Standing</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {assessments.map((record) => (
            <Fragment key={record.id}>
              <tr className={record.effective ? undefined : styles.inactive}>
                <th scope="row">{formatTimestamp(record.assessedAt)}</th>
                <td className={styles.numeric}>{record.rating}</td>
                <td>
                  {sourceLabel(record.sourceType)}
                  {record.recordedByViewer ? <span className={styles.yours}> · yours</span> : null}
                </td>
                <td>
                  <Standing record={record} />
                </td>
                <td>
                  {record.recordedByViewer && record.effective && viewerCanAssessAs ? (
                    <span className={styles.actions}>
                      <button
                        type="button"
                        className={styles.action}
                        onClick={() => setActing({ mode: 'correct', record })}
                      >
                        Correct
                      </button>
                      <button
                        type="button"
                        className={styles.action}
                        onClick={() => setActing({ mode: 'withdraw', record })}
                      >
                        Withdraw
                      </button>
                    </span>
                  ) : (
                    <span className={styles.noActions}>—</span>
                  )}
                </td>
              </tr>
              <EvidenceRow record={record} />
            </Fragment>
          ))}
        </tbody>
      </table>

      {acting?.mode === 'correct' && viewerCanAssessAs ? (
        <AssessmentForm
          employeeId={employeeId}
          employeeName={employeeName}
          attribute={attribute}
          sourceType={viewerCanAssessAs}
          correcting={{ assessmentId: acting.record.id, currentRating: acting.record.rating }}
          /* Closes and refreshes rather than leaving the form's own
             confirmation on screen. From here the refreshed list *is* the
             acknowledgement, and a better one: the corrected record appears
             marked "Replaced by a correction" beside its replacement, which
             is the actual new state rather than a sentence about it. */
          onRecorded={finish}
          onClose={() => setActing(null)}
        />
      ) : null}

      {acting?.mode === 'withdraw' ? (
        <WithdrawalForm
          employeeId={employeeId}
          attributeId={attribute.id}
          attributeName={attribute.name}
          record={acting.record}
          onWithdrawn={finish}
          onClose={() => setActing(null)}
        />
      ) : null}
    </div>
  );
}

/** What a record's lifecycle state means, in words rather than in flags. */
function Standing({ record }: { record: AssessmentRecordResponse }) {
  if (record.recordStatus === 'withdrawn') {
    return (
      <>
        <span className={styles.state}>Withdrawn</span>
        {record.withdrawalReason ? <span className={styles.reason}>{record.withdrawalReason}</span> : null}
      </>
    );
  }
  if (record.supersededByAssessmentId !== null) {
    return (
      <>
        <span className={styles.state}>Replaced by a correction</span>
        <span className={styles.reason}>Kept as a record; no longer counted.</span>
      </>
    );
  }
  return <span className={styles.current}>Counts towards the current figures</span>;
}

/**
 * What supports one record, directly beneath its row. Renders nothing when
 * there is nothing to say — no visible evidence and no hidden item either
 * — rather than an empty "Evidence" heading over blank space.
 */
function EvidenceRow({ record }: { record: AssessmentRecordResponse }) {
  const hiddenCount = record.evidenceCount - record.evidence.length;
  if (record.evidence.length === 0 && hiddenCount === 0) return null;

  return (
    <tr className={styles.evidenceRow}>
      <td colSpan={5}>
        {record.evidence.length > 0 ? (
          <ul className={styles.evidenceList}>
            {record.evidence.map((item) => (
              <li key={item.id}>
                <strong>{evidenceTypeLabel(item.evidenceTypeCode)}</strong>
                {item.visibility === 'restricted' ? <span className={styles.restrictedBadge}>Restricted</span> : null}
                {': '}
                {item.description}
                {item.occurredOn ? ` (${formatCalendarDate(item.occurredOn)})` : ''}
                {item.externalReference ? <span className={styles.reference}> — {item.externalReference}</span> : null}
              </li>
            ))}
          </ul>
        ) : null}
        {hiddenCount > 0 ? (
          <p className={styles.hiddenEvidence}>
            {hiddenCount} further {hiddenCount === 1 ? 'item is' : 'items are'} restricted and not visible to you.
          </p>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * Withdrawing one assessment.
 *
 * A confirmation with a required reason rather than a bare button: this is
 * consequential and irreversible — there is no un-withdraw — and the schema
 * requires the reason, so asking for it is honest rather than ceremonial.
 *
 * The copy states only what the implementation actually does: the record
 * stops counting, and it is kept. It does not claim the assessment
 * "disappears", because it does not.
 */
function WithdrawalForm({
  employeeId,
  attributeId,
  attributeName,
  record,
  onWithdrawn,
  onClose,
}: {
  employeeId: string;
  attributeId: string;
  attributeName: string;
  record: AssessmentRecordResponse;
  onWithdrawn: () => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [state, setState] = useState<{ status: 'editing' | 'submitting' } | { status: 'failed'; message: string }>({
    status: 'editing',
  });
  const submitting = state.status === 'submitting';
  const reasonIsValid = reason.trim().length > 0;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting || !reasonIsValid) return;

    setState({ status: 'submitting' });
    try {
      await apiPost(`/employees/${employeeId}/attributes/${attributeId}/assessments/${record.id}/withdrawal`, {
        reason: reason.trim(),
      });
      onWithdrawn();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} aria-label={`Withdraw your assessment of ${attributeName}`}>
      <p className={styles.confirm}>
        Withdraw your <strong>{sourceLabel(record.sourceType).toLowerCase()}</strong> rating of{' '}
        <strong>{record.rating} out of 20</strong> for {attributeName}, recorded {formatTimestamp(record.assessedAt)}?
      </p>
      <p className={styles.note}>
        The assessment will stop counting towards the composite, the confidence in it and the trajectory. It is not
        deleted: it stays in this list, marked as withdrawn, with the reason you give. This cannot be undone.
      </p>

      <div className={styles.field}>
        <label htmlFor={`withdrawal-reason-${record.id}`} className={styles.label}>
          Reason <span className={styles.required}>required</span>
        </label>
        <textarea
          id={`withdrawal-reason-${record.id}`}
          className={styles.textarea}
          rows={2}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          disabled={submitting}
          placeholder="Why this assessment should no longer stand"
          maxLength={2000}
        />
      </div>

      {state.status === 'failed' ? (
        <p className={styles.error} role="alert">
          {state.message}
        </p>
      ) : null}

      <div className={styles.formActions}>
        <button type="submit" className={styles.destructive} disabled={submitting || !reasonIsValid}>
          {submitting ? 'Withdrawing…' : 'Withdraw assessment'}
        </button>
        <button type="button" className={styles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}
